import { BuiltInAttribute } from "@orsane/schema/tasks";
import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import StatusSelect from "@/components/property-selectors/StatusSelect";
import { useChangeTaskStatusMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";
import { useBulkTargetIds } from "../useBulkTargetIds";

// Static functions - don't need to be recreated
const getSortValue = ({ row }: { row: Row }) => {
    return row.data.taskStatus?.order || 0;
};

const getGroupHeader = ({ row }: { row: Row }) => {
    return row.data.taskStatus?.name || "None";
};

// Memoized cell component
const StatusCell = memo(function StatusCell({ row, onStatusChange }: { row: Row; onStatusChange: (taskId: number, status: any) => void }) {
    const handleChange = useCallback(
        (_statusId: number, status: any) => {
            onStatusChange(row.data.id, status);
        },
        [row.data.id, onStatusChange]
    );

    return (
        <StatusSelect
            trigger={(content) => <CellButton disabled={row.data.isNewlyCreated}>{content}</CellButton>}
            value={row.data.taskStatusId}
            onChange={handleChange}
            hideTodo={true}
        />
    );
});

export function useStatusColumnBuilder() {
    const { mutate } = useChangeTaskStatusMutation();
    const getTargetIds = useBulkTargetIds();

    // Stable mutation handler
    const handleStatusChange = useCallback(
        (taskId: number, status: any) => {
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    taskStatus: status,
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.taskStatus,
                    header: "Status",
                    isVisible: isVisible,
                    cssColDef: "160px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <StatusCell row={row} onStatusChange={handleStatusChange} />,
                };
            },
            [handleStatusChange]
        ),
    };
}
